import { useState } from 'react';
import { toast } from 'sonner';

import { AssistenteDeEtapas } from '@/ui/AssistenteDeEtapas';
import { AvisoDeRascunho } from '@/ui/AvisoDeRascunho';
import { FieldTooltip } from '@/ui/FieldTooltip';
import { FormFieldWrapper } from '@/ui/FormField';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { MoneyInput } from '@/ui/MoneyInput';
import { MonthSelect } from '@/ui/MonthSelect';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { RodapeDoFormularioEmModal } from '@/ui/forms/RodapeDoFormularioEmModal';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { MultiSelectCombobox } from '@/ui/base/multi-select-combobox';
import { SearchableSelect } from '@/ui/base/searchable-select';

import { AmostraSuspensa } from '../AmostraSuspensa';
import { ComponentShowcase } from '../ComponentShowcase';
import { MEDIDAS } from '../medidas';

const DRES = [
  'Butantã',
  'Campo Limpo',
  'Capela do Socorro',
  'Freguesia/Brasilândia',
  'Guaianases',
  'Ipiranga',
  'Itaquera',
  'Jaçanã/Tremembé',
  'Penha',
  'Pirituba/Jaraguá',
  'Santo Amaro',
  'São Mateus',
  'São Miguel',
].map((nome) => ({ value: nome.toLowerCase(), label: nome }));

const ETAPAS = [
  { id: 'dados', titulo: 'Dados', descricao: 'Quem é o credenciado.' },
  { id: 'veiculo', titulo: 'Veículo', descricao: 'O veículo que fará o transporte.' },
  { id: 'revisao', titulo: 'Revisão', descricao: 'Confira antes de enviar.' },
] as const;

/** Nada desta aba grava: o aviso diz isso a quem clicar em enviar. */
function avisarQueNadaFoiEnviado() {
  toast('Nada foi enviado', {
    description: 'Na tela real, este botão chama a API. A amostra não grava.',
  });
}

/** As amostras de formulário que vêm depois das molduras (FormPage, FormSection…). */
export function AmostrasDeFormulario() {
  const [valor, definirValor] = useState('1234.56');
  const [mes, definirMes] = useState('');
  const [dre, definirDre] = useState<string | null>(null);
  // Já com duas escolhidas: o texto de exemplo, sem escolha, reprova no contraste.
  const [dres, definirDres] = useState<string[]>(['campo limpo', 'ipiranga']);
  const [modalAberto, definirModalAberto] = useState(false);
  const [comRascunho, definirComRascunho] = useState(true);
  const [etapa, definirEtapa] = useState(0);
  const [concluidas, definirConcluidas] = useState<ReadonlySet<number>>(() => new Set());

  return (
    <>
      <ComponentShowcase
        id="field-tooltip"
        title="FieldTooltip"
        description="Ícone de ajuda ao lado do rótulo, com a explicação na dica (mouse ou foco). Só para o que o rótulo não consegue dizer em poucas palavras."
        code={`<Label className="flex items-center gap-1">
  Dias operados <FieldTooltip text="Dias letivos em que a OS rodou na competência." />
</Label>`}
        propsTable={[['text', 'string', '-', 'A explicação mostrada na dica']]}
      >
        <div className="flex items-center gap-1 text-sm font-medium text-foreground">
          Dias operados
          <FieldTooltip text="Dias letivos em que a Ordem de Serviço rodou na competência." />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="aviso-de-rascunho"
        title="AvisoDeRascunho"
        description="Aviso no topo do formulário quando há rascunho guardado neste navegador: restaurar ou descartar. Os quatro formulários de cadastro usam."
        code={`{temRascunho && (
  <AvisoDeRascunho gravadoEm={gravadoEm} aoRestaurar={restaurar} aoDescartar={descartar} />
)}`}
        propsTable={[
          ['aoRestaurar', '() => void', '-', 'Preenche o formulário com o rascunho'],
          ['aoDescartar', '() => void', '-', 'Apaga o rascunho'],
          ['gravadoEm', 'Date | null', '-', 'Quando foi guardado; sem ele, sem "Salvo em…"'],
        ]}
      >
        <div className="w-full">
          {comRascunho ? (
            <AvisoDeRascunho
              gravadoEm={new Date()}
              aoRestaurar={() => definirComRascunho(false)}
              aoDescartar={() => definirComRascunho(false)}
            />
          ) : (
            <Button variant="outline" size="sm" onClick={() => definirComRascunho(true)}>
              Mostrar o aviso de novo
            </Button>
          )}
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="money-input"
        title="MoneyInput"
        description="Campo de dinheiro em real: os dígitos entram pelos centavos e o campo devolve o valor na forma do banco (“1234.56”), pronto para a API — nunca a máscara da tela. Com allowNegative, um botão troca o sinal."
        code={`<MoneyInput id="valor" value={valor} onChange={setValor} aria-label="Valor mensal" />
<MoneyDisplay value={valor} />`}
        propsTable={[
          ['value', 'string', '-', 'Valor na forma do banco ("1234.56"); vazio = sem valor'],
          ['onChange', '(valorDecimal: string) => void', '-', 'Recebe a forma do banco'],
          ['id', 'string', '-', 'Id do campo (para o rótulo)'],
          ['placeholder', 'string', '-', 'Texto de exemplo'],
          ['disabled', 'boolean', 'false', 'Desabilita o campo'],
          ['allowNegative', 'boolean', 'false', 'Permite valor negativo (dedução, estorno)'],
          ['aria-label', 'string', '-', 'Nome acessível, quando não há rótulo visível'],
          ['className', 'string', '-', 'Classes do campo'],
        ]}
      >
        <div className="flex w-full max-w-xs flex-col gap-2">
          <Label htmlFor="ds-valor-mensal">Valor mensal</Label>
          <MoneyInput id="ds-valor-mensal" value={valor} onChange={definirValor} />
          <p className="text-xs text-muted-foreground">
            Exibido: <MoneyDisplay value={valor} className="font-medium text-foreground" /> · vai
            para a API: <code className="font-mono">{valor || '(vazio)'}</code>
          </p>
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="month-select"
        title="MonthSelect"
        description="Seletor de mês dos últimos N meses (padrão 12), com “Todos os meses”. Para filtro de listagem com atalhos e grade de ano, o MonthFilter."
        code={`<MonthSelect label="Mês" value={mes} onChange={setMes} />`}
        propsTable={[
          ['label', 'string', '-', 'Rótulo acima do seletor'],
          ['value', 'string', '-', '"YYYY-MM", ou "" para todos os meses'],
          ['onChange', '(value: string) => void', '-', 'Recebe o mês escolhido'],
          ['monthsBack', 'number', '12', 'Quantos meses antes do atual'],
          ['width', 'string', '-', 'Largura do gatilho'],
          ['ariaLabel', 'string', 'label', 'Nome acessível próprio'],
        ]}
      >
        <MonthSelect label="Mês" value={mes} onChange={definirMes} />
      </ComponentShowcase>

      <ComponentShowcase
        id="searchable-select"
        title="SearchableSelect"
        description="Seleção única com digitação: obrigatória acima de dez opções ou em lista que cresce. Filtra no navegador com options, ou no servidor com onSearchChange."
        code={`<SearchableSelect
  options={dres}
  value={dre}
  onChange={setDre}
  placeholder="Selecione a DRE"
  ariaLabel="DRE"
/>`}
        propsTable={[
          [
            'options',
            'SearchableSelectOption[]',
            '-',
            'value, label, code?, description?, inactive?',
          ],
          ['value', 'string | null', '-', 'Valor escolhido'],
          ['onChange', '(value: string | null) => void', '-', 'Nulo = limpou'],
          ['onSearchChange', '(busca: string) => void', '-', 'Liga a busca no servidor'],
          ['placeholder', 'string', '"Selecionar..."', 'Texto sem escolha'],
          ['clearable', 'boolean', 'true', 'Permite limpar'],
          ['ariaLabel', 'string', '-', 'Nome acessível do gatilho'],
          ['modal', 'boolean', 'false', 'true dentro de Dialog'],
        ]}
      >
        <SearchableSelect
          options={DRES}
          value={dre}
          onChange={definirDre}
          placeholder="Selecione a DRE"
          ariaLabel="DRE"
          triggerClassName="w-full sm:w-[260px]"
        />
      </ComponentShowcase>

      <ComponentShowcase
        id="multi-select-combobox"
        title="MultiSelectCombobox"
        description="Seleção múltipla com digitação (filtro de várias DREs, por exemplo). O gatilho é combobox e precisa de ariaLabel: o texto visível não lhe dá nome."
        code={`<MultiSelectCombobox
  options={dres}
  selected={escolhidas}
  onChange={setEscolhidas}
  placeholder="Todas as DREs"
  ariaLabel="DREs"
/>`}
        propsTable={[
          ['options', 'MultiSelectOption[]', '-', 'value, label, code?'],
          ['selected', 'string[]', '-', 'Valores escolhidos'],
          ['onChange', '(selected: string[]) => void', '-', 'Recebe a seleção inteira'],
          ['placeholder', 'string', '"Selecionar..."', 'Texto sem escolha'],
          ['renderSelectedText', '(n: number) => string', '-', 'Texto com N escolhidos'],
          ['clearLabel', 'string', '"Limpar seleção"', 'Rótulo do limpar'],
          ['ariaLabel', 'string', '-', 'Nome acessível do gatilho (obrigatório na prática)'],
        ]}
      >
        <div className="flex w-full flex-col gap-2">
          <MultiSelectCombobox
            options={DRES}
            selected={dres}
            onChange={definirDres}
            placeholder="Todas as DREs"
            ariaLabel="DREs"
            triggerClassName="w-full sm:w-[260px]"
          />
          <AmostraSuspensa
            item="multi-select-combobox"
            medida={MEDIDAS.textoDeExemploDoMultiSelect}
          />
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="responsive-modal"
        title="ResponsiveModal e RodapeDoFormularioEmModal"
        description="Caixa de diálogo no computador e gaveta no celular, com o rodapé fixo (Cancelar e Salvar) registrado pelo RodapeDoFormularioEmModal de dentro do formulário. Com confirmOnClose, fechar pede confirmação para não perder o que foi digitado."
        code={`<ResponsiveModal open={aberto} onOpenChange={setAberto} title="Novo condutor" confirmOnClose>
  <form id="form-condutor" onSubmit={enviar}>
    {/* campos */}
    <RodapeDoFormularioEmModal formId="form-condutor" aoCancelar={() => setAberto(false)} salvando={isPending} />
  </form>
</ResponsiveModal>`}
        propsTable={[
          ['open', 'boolean', '-', 'Aberto ou fechado'],
          ['onOpenChange', '(open: boolean) => void', '-', 'Pedido de abrir ou fechar'],
          ['title', 'ReactNode', '-', 'Título'],
          ['description', 'ReactNode', '-', 'Frase abaixo do título'],
          ['footer', 'ReactNode', '-', 'Rodapé próprio (sem o RodapeDoFormularioEmModal)'],
          ['desktopMaxWidth', 'string', '-', 'Largura máxima no computador'],
          ['confirmOnClose', 'boolean', 'false', 'Fechar pede confirmação'],
          [
            'RodapeDoFormularioEmModal',
            '{ formId, aoCancelar, salvando?, rotuloDeSalvar?, inline? }',
            '-',
            'Cancelar e Salvar do formulário',
          ],
        ]}
      >
        <Button variant="outline" onClick={() => definirModalAberto(true)}>
          Abrir formulário em modal
        </Button>
        <ResponsiveModal
          open={modalAberto}
          onOpenChange={definirModalAberto}
          title="Novo contato de exemplo"
          description="Amostra da documentação: Salvar não grava."
        >
          <form
            id="ds-form-modal"
            onSubmit={(e) => {
              e.preventDefault();
              avisarQueNadaFoiEnviado();
            }}
            className="space-y-4"
          >
            <FormFieldWrapper label="Nome" htmlFor="ds-modal-nome" required>
              <Input id="ds-modal-nome" placeholder="Pessoa Exemplo Um" />
            </FormFieldWrapper>
            <RodapeDoFormularioEmModal
              formId="ds-form-modal"
              aoCancelar={() => definirModalAberto(false)}
            />
          </form>
        </ResponsiveModal>
      </ComponentShowcase>

      <ComponentShowcase
        id="assistente-de-etapas"
        title="AssistenteDeEtapas"
        description="Formulário em etapas: indicador de progresso, Voltar e Avançar (que valida a etapa), e o botão de conclusão na última. Controlado por quem chama; só etapa concluída vira botão no indicador."
        code={`<AssistenteDeEtapas
  etapas={etapas}
  etapaAtual={etapa}
  concluidas={concluidas}
  aoAvancar={async () => form.trigger(camposDaEtapa)}
  aoVoltar={() => setEtapa((e) => e - 1)}
  aoIrPara={setEtapa}
  rotuloDaConclusao="Enviar inscrição"
  aoConcluir={enviar}
  rotuloAcessivel="Etapas do autocadastro"
>
  {conteudoDaEtapa}
</AssistenteDeEtapas>`}
        propsTable={[
          ['etapas', 'readonly EtapaDoAssistente[]', '-', 'id, titulo, descricao?'],
          ['etapaAtual', 'number', '-', 'Índice da etapa corrente (base 0)'],
          ['concluidas', 'ReadonlySet<number>', '-', 'Etapas já concluídas'],
          ['aoAvancar', '() => boolean | Promise<boolean>', '-', 'Valida; false não avança'],
          ['aoVoltar', '() => void', '-', 'Volta uma etapa, sem validar'],
          ['aoIrPara', '(indice: number) => void', '-', 'Sem ela, o indicador não navega'],
          ['rotuloDaConclusao', 'string', '-', 'Botão da última etapa'],
          ['aoConcluir', '() => void', '-', 'Conclusão'],
          ['rotuloAcessivel', 'string', '-', 'Nome da lista de etapas'],
          ['tempoEstimado', 'string', '-', 'Ex.: "≈ 8 min"; some abaixo de sm'],
          ['cabecalho', 'ReactNode', '-', 'Acima do indicador (rascunho, avisos)'],
          ['acoesSecundarias', 'ReactNode', '-', 'Entre Voltar e o botão principal'],
          ['children', 'ReactNode', '-', 'O conteúdo da etapa corrente'],
        ]}
      >
        <div className="w-full">
          <AssistenteDeEtapas
            etapas={ETAPAS}
            etapaAtual={etapa}
            concluidas={concluidas}
            aoAvancar={() => {
              definirConcluidas((c) => new Set([...c, etapa]));
              definirEtapa((e) => Math.min(e + 1, ETAPAS.length - 1));
              return true;
            }}
            aoVoltar={() => definirEtapa((e) => Math.max(e - 1, 0))}
            aoIrPara={definirEtapa}
            rotuloDaConclusao="Enviar"
            aoConcluir={avisarQueNadaFoiEnviado}
            rotuloAcessivel="Etapas do formulário de exemplo"
          >
            <p className="text-sm text-muted-foreground">
              Conteúdo da etapa “{ETAPAS[etapa]?.titulo}”.
            </p>
          </AssistenteDeEtapas>
        </div>
      </ComponentShowcase>
    </>
  );
}
